/**
 * JWT Expiration Hook
 * Monitors JWT token expiration and provides automatic redirect functionality
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { browserJWTService } from '../services/browserJWTService';

/**
 * Custom hook to monitor JWT token expiration
 * @param {string} token - JWT token to monitor
 * @param {Object} options - Configuration options
 * @returns {Object} - Hook state and methods
 */
export const useJWTExpiration = (token, options = {}) => {
  const {
    onExpire = null,           // Callback when token expires
    onWarning = null,          // Callback when warning threshold is reached
    warningThreshold = 60,     // Warning threshold in seconds (default: 1 minute)
    checkInterval = 5000,      // Check interval in milliseconds (default: 5 seconds)
    autoRedirect = true,       // Whether to auto-redirect on expiration
    redirectUrl = '/',         // URL to redirect to on expiration
    redirectDelay = 3000,      // Delay before redirect in milliseconds
    enableWarnings = true      // Whether to show warnings
  } = options;

  const [isValid, setIsValid] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [tokenData, setTokenData] = useState(null);

  const intervalRef = useRef(null);
  const redirectTimeoutRef = useRef(null);
  const warningShownRef = useRef(false);

  /**
   * Handle token expiration
   */
  const handleTokenExpiration = useCallback(() => {
    setIsExpired(true);
    setIsValid(false);
    setTimeRemaining(0);

    // Call custom expiration callback
    if (onExpire) {
      onExpire();
    }

    // Auto-redirect if enabled
    if (autoRedirect) {
      redirectTimeoutRef.current = setTimeout(() => {
        window.location.href = redirectUrl;
      }, redirectDelay);
    }
  }, [onExpire, autoRedirect, redirectUrl, redirectDelay]);

  /**
   * Validate and extract token data
   */
  const validateToken = useCallback(() => {
    if (!token) {
      setIsValid(false);
      setTokenData(null);
      setTimeRemaining(null);
      return null;
    }

    const validation = browserJWTService.validateJWTToken(token);
    
    if (!validation.success) {
      setIsValid(false);
      setTokenData(null);
      setTimeRemaining(null);
      
      // If token is expired, trigger expiration handling
      if (validation.code === 'token_expired') {
        handleTokenExpiration();
      }
      
      return null;
    }

    setIsValid(true);
    setTokenData(validation);
    setTimeRemaining(validation.timeRemaining);
    
    return validation;
  }, [token, handleTokenExpiration]);

  /**
   * Handle warning threshold reached
   */
  const handleWarningThreshold = useCallback((remaining) => {
    if (enableWarnings && !warningShownRef.current && remaining <= warningThreshold) {
      setShowWarning(true);
      warningShownRef.current = true;

      // Call custom warning callback
      if (onWarning) {
        onWarning(remaining);
      }
    }
  }, [enableWarnings, warningThreshold, onWarning]);

  /**
   * Monitor token expiration
   */
  const monitorToken = useCallback(() => {
    const validation = validateToken();
    
    if (!validation || !validation.success) {
      return;
    }

    const remaining = validation.timeRemaining;
    
    if (remaining <= 0) {
      handleTokenExpiration();
      return;
    }

    // Check warning threshold
    handleWarningThreshold(remaining);

    setTimeRemaining(remaining);
  }, [validateToken, handleTokenExpiration, handleWarningThreshold]);

  /**
   * Start monitoring
   */
  const startMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Initial validation
    monitorToken();

    // Set up interval monitoring
    intervalRef.current = setInterval(monitorToken, checkInterval);
  }, [monitorToken, checkInterval]);

  /**
   * Stop monitoring
   */
  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Reset warning state
   */
  const resetWarning = useCallback(() => {
    setShowWarning(false);
    warningShownRef.current = false;
  }, []);

  /**
   * Format time remaining for display
   */
  const formatTimeRemaining = useCallback((seconds) => {
    if (seconds === null || seconds === undefined) return null;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }, []);

  /**
   * Get expiration status message
   */
  const getStatusMessage = useCallback(() => {
    if (isExpired) {
      return 'Token has expired';
    } else if (showWarning) {
      return `Token expires in ${formatTimeRemaining(timeRemaining)}`;
    } else if (isValid && timeRemaining !== null) {
      return `Token valid for ${formatTimeRemaining(timeRemaining)}`;
    } else {
      return 'Token status unknown';
    }
  }, [isExpired, showWarning, isValid, timeRemaining, formatTimeRemaining]);

  // Start monitoring when token changes
  useEffect(() => {
    if (token) {
      startMonitoring();
    } else {
      stopMonitoring();
    }

    return () => {
      stopMonitoring();
    };
  }, [token]); // Only depend on token, not the functions

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMonitoring();
    };
  }, []); // Empty dependency array for unmount cleanup

  return {
    // State
    isValid,
    isExpired,
    timeRemaining,
    showWarning,
    tokenData,
    
    // Methods
    validateToken,
    startMonitoring,
    stopMonitoring,
    resetWarning,
    formatTimeRemaining,
    getStatusMessage,
    
    // Computed values
    statusMessage: getStatusMessage(),
    formattedTimeRemaining: formatTimeRemaining(timeRemaining),
    isNearExpiration: timeRemaining !== null && timeRemaining <= warningThreshold
  };
};

/**
 * Higher-order component to add JWT expiration monitoring
 */
export const withJWTExpiration = (WrappedComponent, jwtOptions = {}) => {
  return function JWTMonitoredComponent(props) {
    const { token, ...otherProps } = props;
    const jwtHook = useJWTExpiration(token, jwtOptions);

    return (
      <WrappedComponent 
        {...otherProps} 
        jwt={jwtHook}
      />
    );
  };
};

export default useJWTExpiration;
